import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import qs from 'qs';

import { useDebounce, useLazyLoad } from '@techchange/components';

const axiosClient = axios.create({
	withCredentials: true,
	paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
});

const SEARCH_TIMEOUT = 450;
const SEARCH_RESULTS_LIMIT = 15;

/**
 * React hook that keeps track of a paginated list of search results.
 *
 * @param {Object} params  The base query params.
 * @param {String} nodeID  The unique ID of the DOM element to listen for scroll events.
 *
 * @returns {Object} loadState          The lazy load state.
 * @returns {Number} loadState.offset   The current offset.
 * @returns {Bool} loadState.loading    True, if the data is loading.
 * @returns {Bool} loadState.loadedAll  True, if all data has loaded.
 */
const usePaginatedSearch = (params = {}, nodeID) => {

	// Keep track of the most recent API search request (to avoid race conditions from old searches)
	const lastSearchAPICall = useRef();

	const [searchState, setSearchState] = useState({
		isSearching: false,
		searchText: '',
		results: null,
		error: false,
		offset: 0,
	});

	const [loadState, setLoadState] = useLazyLoad(nodeID);

	// Keep track of debounced search text to prevent too many API search requests at once
	const debouncedSearchText = useDebounce(searchState.searchText, SEARCH_TIMEOUT);

	// Callback to handle changes to the search text
	const onSearchChange = (text) => {

		setSearchState({
			isSearching: true,
			searchText: text,
			results: null,
			error: false,
			offset: 0,
		});

		setLoadState((prevLoadState) => ({
			...prevLoadState,
			ready: false,
			loading: false,
			loadedAll: false,
		}));

	};

	// Callback to handle initializing the search state on focus
	const onSearchFocus = () => {

		setSearchState((prevSearchState) => ({
			...prevSearchState,
			isSearching: true,
		}));

	};

	// Callback to handle resetting the search state
	const onSearchReset = () => {

		setSearchState({
			isSearching: false,
			searchText: '',
			results: null,
			error: false,
			offset: 0,
		});

		setLoadState((prevLoadState) => ({
			...prevLoadState,
			ready: false,
			loading: false,
			loadedAll: false,
		}));

	};

	// Handles performing the search
	const search = (text) => {

		// Update the query params to include the offset
		const finalQueryParams = {
			...params,
			limit: SEARCH_RESULTS_LIMIT,
			offset: searchState.offset,
			sort: '-created_at',
		};

		if (text) {

			// Update the query params to include the search
			finalQueryParams.search = {
				course: {
					slug: text,
					title: text,
				},
				session: {
					slug: text,
					title: text,
				},
			};

		}

		// Fire off the API search request!
		const promise = axiosClient.get('/api/v2/enrollments', { params: finalQueryParams });

		// Keep track of this one as the most recent API search request!
		lastSearchAPICall.current = promise;

		// Handle the response of the API search request
		promise.then(({ data }) => {

			// Check to make sure that this API search request is the most recent
			if (promise !== lastSearchAPICall.current) {
				return;
			}

			// Remove any sessions that may be null from the results
			const nonNullSessions = data.filter((item) => item.session);

			setSearchState((prevSearchState) => ({
				...prevSearchState,
				results: prevSearchState.results
					? prevSearchState.results.concat(nonNullSessions)
					: nonNullSessions,
				offset: prevSearchState.offset + SEARCH_RESULTS_LIMIT,
			}));

			setLoadState((prevLoadState) => ({
				...prevLoadState,
				ready: true,
				loading: false,
				loadedAll: data.length < SEARCH_RESULTS_LIMIT,
			}));

		// Handle API errors related to the search
		}).catch((error) => {

			// Check to make sure that this API search request is the most recent
			if (promise !== lastSearchAPICall.current) {
				return;
			}

			console.error(error);

			setSearchState((prevSearchState) => ({
				...prevSearchState,
				error: true,
			}));

		});

	};

	// Handle new searches when the search text changes
	useEffect(() => {

		if (!searchState.isSearching) {
			return;
		}

		search(debouncedSearchText);

	}, [debouncedSearchText, searchState.isSearching]);

	// Handle lazy loads triggered by scrolling down the list of search results
	useEffect(() => {

		if (!loadState.loading) {
			return;
		}

		search(debouncedSearchText);

	}, [loadState.loading]);

	return [searchState, loadState, onSearchChange, onSearchFocus, onSearchReset];

};

export default usePaginatedSearch;
